import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { relTime } from "@/lib/api";
import PageHeader from "@/components/layout/PageHeader";
import { INBOX } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Send, Sparkles, Zap, Filter, ArrowRightLeft, Phone, Check, Archive, ArchiveRestore, CheckSquare, Trash2, X, UserPlus, Search } from "lucide-react";
import ChannelIcon, { CHANNEL_COLORS, channelLabel } from "../components/ChannelIcon";
import ContactAvatar from "../components/ContactAvatar";
import DateRangeFilter from "../components/DateRangeFilter";

const CATEGORIES = ["Lead", "Support", "General Inquiry", "Complaint", "Partnership"];
const CHANNELS = ["chat", "email", "whatsapp", "facebook", "instagram", "google_lead"];
const SIM_CHANNELS = CHANNELS.slice(0, 5);

export default function Inbox() {
  const [selectedId, setSelectedId] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [isNote, setIsNote] = useState(false);
  const [channelFilter, setChannelFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [deptFilter, setDeptFilter] = useState("all");
  const [showArchived, setShowArchived] = useState(false);
  const [showSimulate, setShowSimulate] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedConvIds, setSelectedConvIds] = useState(new Set());
  const [search, setSearch] = useState("");
  const [range, setRange] = useState({ from: null, to: null, preset: null });
  const qc = useQueryClient();
  const { user } = useAuth();
  const isAdmin = user?.role === "company_admin" || user?.role === "division_manager";

  const { data: depts = [] } = useQuery({ queryKey: ["depts"], queryFn: () => api.get("/departments").then((r) => r.data) });

  const { data: convs = [] } = useQuery({
    queryKey: ["convs", channelFilter, statusFilter, deptFilter, showArchived, search, range.from, range.to],
    queryFn: () => api.get("/inbox/conversations", { params: {
      channel: channelFilter !== "all" ? channelFilter : undefined,
      status: statusFilter !== "all" ? statusFilter : undefined,
      department_id: (deptFilter !== "all" && deptFilter !== "mine") ? deptFilter : undefined,
      scope: deptFilter === "mine" ? "mine" : undefined,
      archived: showArchived ? true : undefined,
      q: search || undefined,
      date_from: range.from || undefined,
      date_to: range.to || undefined,
    }}).then((r) => r.data),
    refetchInterval: 30000,
  });

  const { data: conv } = useQuery({
    queryKey: ["conv", selectedId],
    queryFn: () => api.get(`/inbox/conversations/${selectedId}`).then((r) => r.data),
    enabled: !!selectedId,
    refetchInterval: 5000,
  });

  // Playbook suggestions — keyword-matched canned templates for the last inbound message
  const { data: playbookSuggestions = [] } = useQuery({
    queryKey: ["playbook-suggestions", selectedId, conv?.messages?.length],
    queryFn: () => api.get(`/inbox/conversations/${selectedId}/playbook-suggestions`).then((r) => r.data),
    enabled: !!selectedId,
    staleTime: 30 * 1000,
  });

  const replyMut = useMutation({
    mutationFn: (payload) => api.post(`/inbox/conversations/${selectedId}/reply`, payload),
    onSuccess: (r) => {
      setReplyText("");
      qc.invalidateQueries({ queryKey: ["conv", selectedId] });
      qc.invalidateQueries({ queryKey: ["convs"] });
      const d = r?.data?._delivery;
      if (d?.channel === "whatsapp") {
        if (d.queued) toast.success(`Queued for WhatsApp send → ${d.to}`);
        else if (d.reason === "no_whatsapp_phone") toast.warning("Reply saved, but no WhatsApp phone on contact — set one to send.");
      }
    },
  });

  const catMut = useMutation({
    mutationFn: (category) => api.post(`/inbox/conversations/${selectedId}/categorize`, { category }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["conv", selectedId] });
      qc.invalidateQueries({ queryKey: ["convs"] });
      if (r.data?.lead) toast.success("Lead added to the Queue");
    },
  });

  const statusMut = useMutation({
    mutationFn: (status) => api.post(`/inbox/conversations/${selectedId}/status`, { status }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["conv", selectedId] }); qc.invalidateQueries({ queryKey: ["convs"] }); },
  });

  // Bulk actions on conversations
  const toggleConv = (id) => setSelectedConvIds((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const clearSelection = () => setSelectedConvIds(new Set());
  const enterSelectMode = () => setSelectMode(true);
  const exitSelectMode = () => { setSelectMode(false); clearSelection(); };
  const bulkConvMut = useMutation({
    mutationFn: (body) => api.post("/inbox/conversations/bulk", body),
    onSuccess: (r) => { toast.success(`Applied to ${r.data.processed} conversation${r.data.processed === 1 ? "" : "s"}`); qc.invalidateQueries({ queryKey: ["convs"] }); qc.invalidateQueries({ queryKey: ["conv", selectedId] }); clearSelection(); },
    onError: (e) => toast.error(e.response?.data?.detail || "Bulk action failed"),
  });

  const selected = convs.find((c) => c.id === selectedId) || conv;

  return (
    <div>
      <PageHeader
        overline="Omnichannel"
        title="Unified Inbox"
        subtitle="Email · Chat · WhatsApp · Facebook · Instagram — all in one place."
        actions={
          <Dialog open={showSimulate} onOpenChange={setShowSimulate}>
            <DialogTrigger asChild>
              <Button data-testid={INBOX.simulateButton} className="rounded-sm bg-primary hover:bg-primary/90"><Sparkles className="h-4 w-4 mr-2" />Simulate Incoming</Button>
            </DialogTrigger>
            <SimulateDialog onClose={() => setShowSimulate(false)} />
          </Dialog>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-12 h-[calc(100dvh-13rem)] max-h-[calc(100dvh-13rem)] min-h-[520px] border-t border-border overflow-hidden">
        {/* Channel filter sidebar */}
        <aside className="lg:col-span-2 border-r border-border p-4 space-y-1 overflow-y-auto min-h-0">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold px-2 py-2">Channels</div>
          <FilterItem active={channelFilter === "all"} onClick={() => setChannelFilter("all")} icon={Zap} label="All channels" />
          {CHANNELS.map((c) => (
            <ChannelFilterItem key={c} active={channelFilter === c} onClick={() => setChannelFilter(c)} channel={c} />
          ))}

          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold px-2 pt-4 pb-2">Status</div>
          {["all", "open", "pending", "resolved", "closed"].map((s) => (
            <FilterItem key={s} active={statusFilter === s} onClick={() => setStatusFilter(s)} icon={Filter} label={s} />
          ))}

          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold px-2 pt-4 pb-2">Department</div>
          <FilterItem active={deptFilter === "all"} onClick={() => setDeptFilter("all")} icon={Filter} label="All" />
          <FilterItem active={deptFilter === "mine"} onClick={() => setDeptFilter("mine")} icon={Filter} label="My departments" />
          {depts.map((d) => (
            <button
              key={d.id}
              onClick={() => setDeptFilter(d.id)}
              data-testid={`inbox-dept-${d.id}`}
              className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-sm text-xs transition-colors ${deptFilter === d.id ? "bg-primary/15 text-white" : "text-muted-foreground hover:bg-secondary/50 hover:text-white"}`}
            >
              <Filter className="h-3.5 w-3.5" />
              <span className="flex-1 text-left truncate">{d.name}</span>
              <span className="text-[9px] uppercase tracking-widest opacity-70">{d.sla_minutes || 240}m</span>
            </button>
          ))}
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold px-2 pt-4 pb-2">View</div>
          <button
            onClick={() => setShowArchived((v) => !v)}
            data-testid="inbox-toggle-archived"
            className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-sm text-xs transition-colors ${showArchived ? "bg-yellow-500/15 text-yellow-400 border border-yellow-500/30" : "text-muted-foreground hover:bg-secondary/50 hover:text-white"}`}
          >
            <Archive className="h-3.5 w-3.5" />
            <span className="flex-1 text-left">{showArchived ? "Archived only" : "Active conversations"}</span>
          </button>
          <button
            onClick={selectMode ? exitSelectMode : enterSelectMode}
            data-testid="inbox-toggle-select"
            className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-sm text-xs transition-colors ${selectMode ? "bg-primary/15 text-primary" : "text-muted-foreground hover:bg-secondary/50 hover:text-white"}`}
          >
            <CheckSquare className="h-3.5 w-3.5" />
            <span className="flex-1 text-left">{selectMode ? "Cancel selection" : "Select multiple"}</span>
          </button>
        </aside>

        {/* Conversation list */}
        <div className="lg:col-span-3 border-r border-border overflow-y-auto min-h-0">
          <div className="p-3 border-b border-border space-y-2 sticky top-0 bg-card z-10">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input data-testid="inbox-search" placeholder="Search conversations…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-8 rounded-sm bg-secondary/40 h-8 text-xs" />
            </div>
            <DateRangeFilter value={range} onChange={setRange} testId="inbox-date" />
          </div>
          {convs.length === 0 && <div className="p-8 text-center text-sm text-muted-foreground">No conversations yet. Click <span className="text-white">Simulate Incoming</span>.</div>}
          {convs.map((c) => {
            const brand = CHANNEL_COLORS[c.channel] || CHANNEL_COLORS.chat;
            const isSelected = selectedConvIds.has(c.id);
            return (
              <div
                key={c.id}
                onClick={() => selectMode ? toggleConv(c.id) : setSelectedId(c.id)}
                data-testid={INBOX.conversationItem}
                className={`w-full text-left p-4 border-b border-border hover:bg-secondary/40 transition-colors cursor-pointer ${selectedId === c.id && !selectMode ? "bg-secondary/70 border-l-2" : "border-l-2 border-l-transparent"} ${isSelected ? "bg-primary/5" : ""}`}
                style={selectedId === c.id && !selectMode ? { borderLeftColor: brand.color } : undefined}
              >
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="flex items-center gap-2 min-w-0">
                    {selectMode && (
                      <Checkbox checked={isSelected} onCheckedChange={() => toggleConv(c.id)} onClick={(e) => e.stopPropagation()} data-testid={`inbox-checkbox-${c.id}`} className="flex-none" />
                    )}
                    <div className="relative flex-none">
                      <ContactAvatar src={c.contact?.avatar_url} name={c.contact?.name} size={26} />
                      <span className="absolute -bottom-1 -right-1">
                        <ChannelIcon channel={c.channel} size={8} badge />
                      </span>
                    </div>
                    <div className="text-sm font-semibold truncate">{c.contact?.name || "Unknown"}</div>
                  </div>
                  <div className="text-[10px] text-muted-foreground flex-none">{relTime(c.last_message_at)}</div>
                </div>
                <div className="text-xs text-muted-foreground truncate">{c.last_message?.text || c.subject}</div>
                <div className="flex items-center gap-1 mt-2 flex-wrap">
                  {c.category && <Badge className="rounded-sm text-[10px] px-1.5 py-0" variant={c.category === "Lead" ? "default" : "secondary"}>{c.category}</Badge>}
                  <Badge variant="outline" className="rounded-sm text-[10px] px-1.5 py-0">{c.status}</Badge>
                  {c.archived && <Badge className="rounded-sm text-[10px] px-1.5 py-0 bg-yellow-500/20 text-yellow-400 border-yellow-500/30"><Archive className="h-2.5 w-2.5 mr-0.5" />archived</Badge>}
                  {c.tags?.includes("escalated") && <Badge className="rounded-sm text-[10px] px-1.5 py-0 bg-yellow-500/20 text-yellow-500 border-yellow-500/30">Escalated</Badge>}
                </div>
              </div>
            );
          })}
        </div>

        {/* Thread pane */}
        <div className="lg:col-span-7 flex flex-col min-h-0 overflow-hidden">
          {!selected ? (
            <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">Select a conversation to view messages</div>
          ) : (
            <>
              <div className="border-b border-border p-4 flex items-center gap-4 flex-wrap flex-none max-h-[120px] overflow-y-auto">
                <div className="relative flex-none">
                  <ContactAvatar src={(conv?.contact || selected.contact)?.avatar_url} name={selected.contact?.name} size={40} />
                  <span className="absolute -bottom-1 -right-1">
                    <ChannelIcon channel={selected.channel} size={10} badge />
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold flex items-center gap-2">
                    {selected.contact?.name}
                    {(conv?.contact || selected.contact)?.id && (
                      <a href={`/contacts?open=${(conv?.contact || selected.contact).id}`} className="text-[10px] uppercase tracking-widest text-primary hover:underline" data-testid="inbox-view-profile-link">View profile</a>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground flex items-center gap-1.5">
                    <span>{selected.contact?.email || selected.contact?.phone}</span>
                    <span>·</span>
                    <span className="capitalize" style={{ color: (CHANNEL_COLORS[selected.channel] || CHANNEL_COLORS.chat).color }}>{channelLabel(selected.channel)}</span>
                    {selected.department_name && <><span>·</span><span>{selected.department_name}</span></>}
                  </div>
                </div>
                {selected.channel === "whatsapp" && (
                  <WhatsAppPhoneEditor contact={conv?.contact || selected.contact} onSaved={() => { qc.invalidateQueries({ queryKey: ["conv", selectedId] }); qc.invalidateQueries({ queryKey: ["convs"] }); }} />
                )}
                <TransferButton conversationId={selected.id} onDone={() => { qc.invalidateQueries({ queryKey: ["conv", selectedId] }); qc.invalidateQueries({ queryKey: ["convs"] }); }} />
                <Select value={selected.category || ""} onValueChange={(v) => catMut.mutate(v)}>
                  <SelectTrigger data-testid={INBOX.categorySelect} className="rounded-sm w-40 bg-secondary/40"><SelectValue placeholder="Category" /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={selected.status} onValueChange={(v) => statusMut.mutate(v)}>
                  <SelectTrigger data-testid={INBOX.statusSelect} className="rounded-sm w-32 bg-secondary/40"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["open", "pending", "resolved", "closed"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex-1 overflow-y-auto min-h-0 p-6 space-y-3 bg-[#0A0A0A]/60">
                {(conv?.messages || []).map((m) => (
                  <div key={m.id} className={`flex ${m.sender_type === "agent" || m.sender_type === "ai" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-lg p-3 rounded-sm text-sm ${m.is_internal_note ? "bg-yellow-500/10 border border-yellow-500/30 text-yellow-50" : m.sender_type === "customer" ? "bg-secondary/80 border border-border" : "bg-primary text-white"}`}>
                      <div className="text-[10px] uppercase tracking-wider mb-1 opacity-70">{m.sender_name} {m.is_internal_note && "· internal note"}</div>
                      <div className="whitespace-pre-wrap">{m.text}</div>
                      <div className="text-[10px] opacity-70 mt-1 flex items-center justify-end gap-1">
                        <span>{relTime(m.created_at)}</span>
                        {(m.sender_type === "agent" || m.sender_type === "ai") && !m.is_internal_note && (
                          <DeliveryStatus status={m.delivery_status} error={m.delivery_error} />
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="border-t border-border p-4 space-y-2 flex-none">
                {playbookSuggestions.length > 0 && !isNote && (
                  <div className="flex items-center gap-2 overflow-x-auto pb-1" data-testid="playbook-suggestions">
                    <span className="text-[9px] uppercase tracking-widest text-primary font-bold whitespace-nowrap">Playbook</span>
                    {playbookSuggestions.map((s) => (
                      <button
                        key={s.template.id}
                        type="button"
                        onClick={() => setReplyText(s.template.body)}
                        title={`Matched: ${s.matched.join(", ")}`}
                        className="flex-none text-[11px] px-2.5 py-1 rounded-sm border border-primary/40 bg-primary/10 text-primary hover:bg-primary/20 transition-colors whitespace-nowrap"
                        data-testid={`playbook-suggestion-${s.template.id}`}
                      >
                        {s.template.title}
                      </button>
                    ))}
                  </div>
                )}
                <div className="flex items-center gap-3">
                  <Switch checked={isNote} onCheckedChange={setIsNote} data-testid={INBOX.internalNoteToggle} />
                  <span className="text-xs text-muted-foreground">{isNote ? "Internal note (team-only)" : "Public reply to customer"}</span>
                </div>
                <div className="flex gap-2">
                  <Textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
                        e.preventDefault();
                        if (replyText.trim() && !replyMut.isPending) {
                          replyMut.mutate({ text: replyText, is_internal_note: isNote });
                        }
                      }
                    }}
                    placeholder={isNote ? "Add a private note… (Enter to save, Shift+Enter for newline)" : "Type your reply… (Enter to send, Shift+Enter for newline)"}
                    className="rounded-sm bg-secondary/40 min-h-[60px] max-h-[120px] resize-none"
                    data-testid={INBOX.replyInput}
                  />
                  <Button
                    onClick={() => replyText.trim() && replyMut.mutate({ text: replyText, is_internal_note: isNote })}
                    disabled={!replyText.trim() || replyMut.isPending}
                    className="rounded-sm bg-primary hover:bg-primary/90 self-end"
                    data-testid={INBOX.replySend}
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {selectedConvIds.size > 0 && (
        <InboxBulkBar
          count={selectedConvIds.size}
          isAdmin={isAdmin}
          showArchived={showArchived}
          onAction={(action) => bulkConvMut.mutate({ conversation_ids: Array.from(selectedConvIds), action })}
          onClose={clearSelection}
        />
      )}
    </div>
  );
}

function InboxBulkBar({ count, isAdmin, showArchived, onAction, onClose }) {
  return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-card border border-primary/40 rounded-sm shadow-xl p-3 flex items-center gap-3 max-w-[95vw]" data-testid="inbox-bulk-bar">
      <div className="text-sm font-semibold flex items-center gap-2">
        <CheckSquare className="h-4 w-4 text-primary" />
        {count} selected
      </div>
      <div className="h-6 w-px bg-border" />
      {!showArchived ? (
        <Button size="sm" variant="outline" className="rounded-sm" data-testid="inbox-bulk-archive-btn" onClick={() => onAction("archive")}>
          <Archive className="h-3.5 w-3.5 mr-1" />Archive
        </Button>
      ) : (
        <Button size="sm" variant="outline" className="rounded-sm" data-testid="inbox-bulk-unarchive-btn" onClick={() => onAction("unarchive")}>
          <ArchiveRestore className="h-3.5 w-3.5 mr-1" />Unarchive
        </Button>
      )}
      <Button size="sm" variant="outline" className="rounded-sm" data-testid="inbox-bulk-resolve-btn" onClick={() => onAction("resolve")}>
        <Check className="h-3.5 w-3.5 mr-1" />Mark resolved
      </Button>
      {isAdmin && (
        <Button size="sm" variant="outline" className="rounded-sm border-red-500/30 text-red-400 hover:text-red-300" data-testid="inbox-bulk-delete-btn" onClick={() => { if (window.confirm(`Delete ${count} conversation(s)? This is permanent — all messages will be lost.`)) onAction("delete"); }}>
          <Trash2 className="h-3.5 w-3.5 mr-1" />Delete
        </Button>
      )}
      <Button size="sm" variant="ghost" className="rounded-sm" onClick={onClose}><X className="h-3.5 w-3.5" /></Button>
    </div>
  );
}

function FilterItem({ active, onClick, icon: Icon, label }) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-sm text-xs capitalize transition-colors ${active ? "bg-primary/15 text-white" : "text-muted-foreground hover:bg-secondary/50 hover:text-white"}`}
    >
      <Icon className="h-3.5 w-3.5" />
      {label}
    </button>
  );
}

function ChannelFilterItem({ active, onClick, channel }) {
  const brand = CHANNEL_COLORS[channel] || CHANNEL_COLORS.chat;
  return (
    <button
      onClick={onClick}
      data-testid={`inbox-channel-${channel}`}
      className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-sm text-xs capitalize transition-colors ${active ? "text-white" : "text-muted-foreground hover:text-white"}`}
      style={active ? { backgroundColor: brand.bg, border: `1px solid ${brand.border}` } : undefined}
    >
      <ChannelIcon channel={channel} size={12} badge />
      <span className="flex-1 text-left">{channelLabel(channel)}</span>
    </button>
  );
}

function SLABadge({ deadline }) {
  const [now, setNow] = React.useState(Date.now());
  React.useEffect(() => { const t = setInterval(() => setNow(Date.now()), 30000); return () => clearInterval(t); }, []);
  if (!deadline) return null;
  const diffMs = new Date(deadline).getTime() - now;
  const overdue = diffMs < 0;
  const abs = Math.abs(diffMs);
  const mins = Math.floor(abs / 60000);
  const hours = Math.floor(mins / 60);
  const label = hours >= 1 ? `${hours}h ${mins % 60}m` : `${mins}m`;
  return (
    <Badge
      data-testid="sla-badge"
      className={`rounded-sm text-[10px] ${overdue ? "bg-red-500/20 text-red-400 border-red-500/40 animate-pulse" : diffMs < 30 * 60 * 1000 ? "bg-yellow-500/20 text-yellow-400 border-yellow-500/40" : "bg-green-500/20 text-green-400 border-green-500/30"}`}
    >
      {overdue ? `SLA breached · ${label} late` : `SLA · ${label} left`}
    </Badge>
  );
}

function TransferButton({ conversationId, onDone }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("agent"); // agent | dept
  const [agentId, setAgentId] = useState("");
  const [deptId, setDeptId] = useState("");
  const [note, setNote] = useState("");

  const { data: users = [] } = useQuery({ queryKey: ["users"], queryFn: () => api.get("/users").then((r) => r.data), enabled: open });
  const { data: depts = [] } = useQuery({ queryKey: ["depts"], queryFn: () => api.get("/departments").then((r) => r.data), enabled: open });

  const mut = useMutation({
    mutationFn: () => {
      const payload = { note };
      if (tab === "agent") payload.agent_id = agentId;
      if (tab === "dept") payload.department_id = deptId;
      return api.post(`/inbox/conversations/${conversationId}/transfer`, payload);
    },
    onSuccess: () => { toast.success("Conversation transferred"); onDone(); setOpen(false); setAgentId(""); setDeptId(""); setNote(""); },
    onError: (e) => toast.error(e.response?.data?.detail || "Transfer failed"),
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="rounded-sm" data-testid="inbox-transfer-button"><ArrowRightLeft className="h-3.5 w-3.5 mr-1" />Transfer</Button>
      </DialogTrigger>
      <DialogContent className="rounded-sm max-w-lg">
        <DialogHeader><DialogTitle className="font-display text-2xl font-extrabold tracking-tight">Transfer Conversation</DialogTitle></DialogHeader>
        <div className="flex gap-1 border border-border rounded-sm p-1 bg-secondary/40 w-fit">
          <button onClick={() => setTab("agent")} className={`px-3 py-1 rounded-sm text-xs ${tab === "agent" ? "bg-primary text-white" : "text-muted-foreground"}`} data-testid="transfer-tab-agent">To agent</button>
          <button onClick={() => setTab("dept")} className={`px-3 py-1 rounded-sm text-xs ${tab === "dept" ? "bg-primary text-white" : "text-muted-foreground"}`} data-testid="transfer-tab-dept">To department</button>
        </div>
        {tab === "agent" ? (
          <div>
            <Label>Assign to agent</Label>
            <Select value={agentId} onValueChange={setAgentId}>
              <SelectTrigger className="rounded-sm mt-1.5 bg-secondary/40" data-testid="transfer-agent-select"><SelectValue placeholder="Pick an agent…" /></SelectTrigger>
              <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} · {u.role?.replace("_", " ")}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        ) : (
          <div>
            <Label>Route to department</Label>
            <Select value={deptId} onValueChange={setDeptId}>
              <SelectTrigger className="rounded-sm mt-1.5 bg-secondary/40" data-testid="transfer-dept-select"><SelectValue placeholder="Pick a department…" /></SelectTrigger>
              <SelectContent>
                {depts.length === 0 ? <div className="p-2 text-xs text-muted-foreground">No departments yet — create one in Settings</div> : depts.map((d) => <SelectItem key={d.id} value={d.id}>{d.name} · {(d.members || []).length} members</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        <div>
          <Label>Handoff note (optional, internal)</Label>
          <Textarea rows={2} className="rounded-sm mt-1.5 bg-secondary/40" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Context for the receiving team…" data-testid="transfer-note" />
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Cancel</Button>
          <Button data-testid="transfer-submit" className="rounded-sm bg-primary" onClick={() => mut.mutate()} disabled={mut.isPending || (tab === "agent" ? !agentId : !deptId)}>Transfer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeliveryStatus({ status, error }) {
  if (!status || status === "pending") {
    return <span title="Queued in LeadifyCRM outbox" className="opacity-70" data-testid="delivery-pending">◔</span>;
  }
  if (status === "dispatched" || status === "sending") {
    return <span title="Picked up by WhatsWizard, sending on WhatsApp Web…" className="opacity-70" data-testid="delivery-dispatched">◐</span>;
  }
  if (status === "sent") {
    return <span title="Sent via WhatsApp Web" data-testid="delivery-sent">✓</span>;
  }
  if (status === "delivered") {
    return <span title="Delivered to recipient" data-testid="delivery-delivered">✓✓</span>;
  }
  if (status === "read") {
    return <span title="Read by recipient" className="text-blue-300" data-testid="delivery-read">✓✓</span>;
  }
  if (status === "failed") {
    return <span title={`Failed: ${error || "unknown"}`} className="text-red-300" data-testid="delivery-failed">⚠ failed</span>;
  }
  return null;
}

function WhatsAppPhoneEditor({ contact, onSaved }) {
  const current = contact?.whatsapp_phone || contact?.phone || "";
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(current);
  React.useEffect(() => { setValue(current); }, [current]);

  const mut = useMutation({
    mutationFn: () => api.patch(`/contacts/${contact.id}`, { whatsapp_phone: value.trim() }),
    onSuccess: () => { toast.success("WhatsApp number saved"); setEditing(false); onSaved?.(); },
    onError: () => toast.error("Couldn't save number"),
  });

  if (!contact?.id) return null;

  const looksDialable = /^[+]?[0-9\s\-()]{6,}$/.test(current);

  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        data-testid="wa-phone-edit"
        className={`text-xs rounded-sm px-2.5 py-1.5 border flex items-center gap-1.5 transition-colors ${looksDialable ? "border-green-500/30 bg-green-500/10 text-green-400 hover:bg-green-500/15" : "border-yellow-500/40 bg-yellow-500/10 text-yellow-400 hover:bg-yellow-500/15"}`}
        title={looksDialable ? "WhatsApp number set for outbound" : "Set a WhatsApp number to enable outbound sending"}
      >
        <Phone className="h-3 w-3" />
        {looksDialable ? current : "Set WhatsApp #"}
      </button>
    );
  }
  return (
    <div className="flex items-center gap-1.5">
      <Input
        className="h-8 rounded-sm bg-secondary/40 w-40 text-xs"
        placeholder="+6598765432"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        data-testid="wa-phone-input"
        autoFocus
        onKeyDown={(e) => { if (e.key === "Enter") mut.mutate(); if (e.key === "Escape") setEditing(false); }}
      />
      <Button size="sm" className="h-8 rounded-sm bg-primary hover:bg-primary/90 px-2" onClick={() => mut.mutate()} disabled={mut.isPending} data-testid="wa-phone-save">
        <Check className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}

function SimulateDialog({ onClose }) {
  const [channel, setChannel] = useState("chat");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [body, setBody] = useState("");
  const qc = useQueryClient();
  const mut = useMutation({
    mutationFn: (payload) => api.post("/inbox/simulate", payload),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["convs"] }); toast.success("Message received"); onClose(); },
  });
  return (
    <DialogContent className="rounded-sm max-w-lg">
      <DialogHeader><DialogTitle className="font-display text-2xl font-extrabold tracking-tight">Simulate Incoming Message</DialogTitle></DialogHeader>
      <div className="space-y-4">
        <div>
          <Label>Channel</Label>
          <Select value={channel} onValueChange={setChannel}>
            <SelectTrigger className="rounded-sm mt-1.5 bg-secondary/40"><SelectValue /></SelectTrigger>
            <SelectContent>{SIM_CHANNELS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Contact name</Label><Input className="mt-1.5 rounded-sm bg-secondary/40" value={name} onChange={(e) => setName(e.target.value)} /></div>
          <div><Label>Email</Label><Input className="mt-1.5 rounded-sm bg-secondary/40" value={email} onChange={(e) => setEmail(e.target.value)} /></div>
        </div>
        <div><Label>Message</Label><Textarea className="mt-1.5 rounded-sm bg-secondary/40" value={body} onChange={(e) => setBody(e.target.value)} rows={4} /></div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onClose} className="rounded-sm">Cancel</Button>
        <Button data-testid={INBOX.simulateSubmit} className="rounded-sm bg-primary" onClick={() => mut.mutate({ channel, contact_name: name || "Anonymous", contact_email: email, body })} disabled={mut.isPending || !body.trim()}>Send</Button>
      </DialogFooter>
    </DialogContent>
  );
}
